import { useMemo } from 'react';
import Decimal from 'decimal.js';
import { dateFormatter as getDateFormatter } from '@wix/crm-financial-formatters';
import { InvoiceStatus } from '@wix/ambassador-wix-quotes-web/types';
import { currencyFormat, dateFormat, localDate } from '../../core';
import { isStatusDisplayed } from '../InvoiceViewV2/Status/status-utils';
import { hasDeposit } from '../DocParts/Totals/Deposit/hasDeposit';
import { ORDER_REFERENCE_CUSTOM_FIELD_KEY } from '../DocParts/InvoiceInfo/OrderReference';
import { mapInvoiceDetailsFromDTO } from './utils';
export var useHooks = function (_a) {
    var _b;
    var businessCustomFields = _a.businessCustomFields, customFields = _a.customFields, customInvoiceNumberLabel = _a.customInvoiceNumberLabel, documentCustomFields = _a.documentCustomFields, footer = _a.footer, invoice = _a.invoice, primarySiteLocale = _a.primarySiteLocale, recurringInvoiceViewEnabled = _a.recurringInvoiceViewEnabled, status = _a.status, t = _a.t;
    var invoiceDetails = useMemo(function () {
        return mapInvoiceDetailsFromDTO({
            invoice: invoice,
            recurringInvoiceViewEnabled: recurringInvoiceViewEnabled,
            status: status,
        });
    }, [invoice, recurringInvoiceViewEnabled, status]);
    var moneyFormatter = useMemo(function () {
        return currencyFormat({
            currency: invoiceDetails.currency,
            locale: invoiceDetails.locale,
        });
    }, [invoiceDetails.currency, invoiceDetails.locale]);
    var dateFormatter = useMemo(function () {
        return primarySiteLocale
            ? getDateFormatter(primarySiteLocale, true)
            : localDate(dateFormat({ locale: invoiceDetails.locale }));
    }, [invoiceDetails.locale, primarySiteLocale]);
    var invoiceSummaryProps = {
        balance: invoiceDetails.balance,
        deposit: invoiceDetails.deposit,
        discount: invoiceDetails.discount,
        fees: invoiceDetails.fees,
        paid: invoiceDetails.paid,
        subtotal: invoiceDetails.subtotal,
        taxes: invoiceDetails.taxes,
        total: invoiceDetails.total,
    };
    var footerNotesProps = {
        footer: {
            firstLabel: footer === null || footer === void 0 ? void 0 : footer.notesLabel,
            secondLabel: footer === null || footer === void 0 ? void 0 : footer.legalTermsLabel,
        },
        metaData: invoiceDetails.metaData,
    };
    var hasValidDeposit = hasDeposit({
        depositAmount: invoiceDetails.deposit.amount,
        paidAmount: invoiceDetails.paid,
    });
    var dueAmount = hasValidDeposit
        ? Decimal.sub(invoiceDetails.deposit.amount, invoiceDetails.paid).toNumber()
        : invoiceDetails.balance;
    var showInvoiceHighlight = invoiceDetails.status !== InvoiceStatus.Paid;
    var showInvoiceStatus = isStatusDisplayed({ status: status, isUoU: true });
    var showFooterNotes = ((footer === null || footer === void 0 ? void 0 : footer.notesLabel) || invoiceDetails.metaData.notes) &&
        ((footer === null || footer === void 0 ? void 0 : footer.legalTermsLabel) || invoiceDetails.metaData.legalTerms);
    var orderReferenceCustomField = customFields === null || customFields === void 0 ? void 0 : customFields.find(function (_a) {
        var originCustomFieldKey = _a.originCustomFieldKey;
        return originCustomFieldKey === ORDER_REFERENCE_CUSTOM_FIELD_KEY;
    });
    var orderReference = (orderReferenceCustomField === null || orderReferenceCustomField === void 0 ? void 0 : orderReferenceCustomField.value)
        ? t('invoice-view.order-number.label', {
            orderNumber: (_b = orderReferenceCustomField.value.value) !== null && _b !== void 0 ? _b : '',
        })
        : undefined;
    var invoiceNumberLabel = customInvoiceNumberLabel
        ? t('invoice_view.custom-invoice-number-label', {
            customInvoiceNumberLabel: customInvoiceNumberLabel,
            number: invoiceDetails.invoiceNumber,
        })
        : t('invoice_view.invoice_number', {
            number: invoiceDetails.invoiceNumber,
        });
    var documentCustomFieldsContent = useMemo(function () {
        var _a;
        return (_a = documentCustomFields === null || documentCustomFields === void 0 ? void 0 : documentCustomFields.filter(function (_a) {
            var value = _a.value, displayName = _a.displayName;
            return !!value && !!displayName;
        }).map(function (_a) {
            var displayName = _a.displayName, value = _a.value;
            return t('price-quote.view.custom-field', {
                displayName: displayName,
                value: value,
            });
        })) !== null && _a !== void 0 ? _a : [];
    }, [documentCustomFields, t]);
    var businessDetailsCustomFieldsContent = useMemo(function () {
        var _a;
        return (_a = businessCustomFields === null || businessCustomFields === void 0 ? void 0 : businessCustomFields.filter(function (_a) {
            var value = _a.value, displayName = _a.displayName;
            return !!value && !!displayName;
        }).map(function (_a) {
            var displayName = _a.displayName, value = _a.value;
            return t('price-quote.view.custom-field', {
                displayName: displayName,
                value: value,
            });
        })) !== null && _a !== void 0 ? _a : [];
    }, [businessCustomFields, t]);
    return {
        businessDetailsCustomFieldsContent: businessDetailsCustomFieldsContent,
        dateFormatter: dateFormatter,
        documentCustomFieldsContent: documentCustomFieldsContent,
        dueAmount: dueAmount,
        footerNotesProps: footerNotesProps,
        invoiceDetails: invoiceDetails,
        invoiceNumberLabel: invoiceNumberLabel,
        invoiceSummaryProps: invoiceSummaryProps,
        moneyFormatter: moneyFormatter,
        orderReference: orderReference,
        showFooterNotes: showFooterNotes,
        showInvoiceHighlight: showInvoiceHighlight,
        showInvoiceStatus: showInvoiceStatus,
    };
};
//# sourceMappingURL=useHooks.js.map